import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PhoneOutgoing, Mic, X, Sparkles, CheckCircle2, Loader2, ArrowRight, ArrowLeft,
  Headphones, Phone, RefreshCw, Heart, Wrench, Stethoscope, Smile,
  User, Mail, Calendar, Clock, Pencil, MailCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Voice-AI placeholder UI — industry-aware.
   Step 1 — pick industry (Physio · Dental · Spa · Auto Repair)
   Step 2 — animated mock inbound call WITH live field extraction + Call IQ + email confirmation
   Step 3 — CTAs: Start live conversation (placeholder) · Get a call back (form) · Pick another industry */

const INDUSTRIES = {
  physio: {
    id: "physio",
    label: "Physiotherapy",
    Icon: Heart,
    accent: { from: "#0ea5e9", to: "#2563eb" },
    blurb: "Inbound booking for a sports clinic.",
    voice: "Aria · warm, clinical",
    business: "Apex Physio · Beachview",
    transcript: [
      { who: "ring",   txt: "Ring 1…" },
      { who: "ring",   txt: "Ring 2…" },
      { who: "ring",   txt: "Ring 3 — AI picks up." },
      { who: "agent",  txt: "Hi, this is Aria at Apex Physio Beachview — how can I help?" },
      { who: "caller", txt: "My shoulder's been bothering me for a few weeks. Can I get in this week?", extract: { reason: "Shoulder pain · weeks" } },
      { who: "agent",  txt: "Sorry to hear that. Can I grab your name first?" },
      { who: "caller", txt: "Yes — it's Tom Bennett.", extract: { name: "Tom Bennett" } },
      { who: "agent",  txt: "Thanks Tom. Sarah our shoulder specialist has Wednesday 11am or Friday 4pm — which works?" },
      { who: "caller", txt: "Wednesday 11am please.", extract: { apt_date: "Wed · Apr 15", apt_time: "11:00 AM", clinician: "Sarah Chen" } },
      { who: "agent",  txt: "Best email and phone to send the confirmation?" },
      { who: "caller", txt: "tom.bennett@gmail.com — 416-555-0102.", extract: { email: "tom.bennett@gmail.com", phone: "+1 (416) 555-0102" } },
      { who: "agent",  txt: "Booked. I'll email the confirmation and intake form. See you Wednesday." },
    ],
    iq: {
      classification: "booked",
      answered_by:    "AI",
      patient_type:   "new",
      booking_type:   "Shoulder Assessment",
      summary: "Tom Bennett — new patient — called about a multi-week shoulder issue. AI booked him with Sarah Chen for Wed 11:00 AM and emailed the intake form. Call closed in 1m 18s.",
    },
  },
  dental: {
    id: "dental",
    label: "Dental",
    Icon: Stethoscope,
    accent: { from: "#10b981", to: "#0d9488" },
    blurb: "Emergency triage + booking for a dental practice.",
    voice: "Eva · calm, reassuring",
    business: "Bright Smile Dental",
    transcript: [
      { who: "ring",   txt: "Ring 1…" },
      { who: "ring",   txt: "Ring 2…" },
      { who: "ring",   txt: "Ring 3 — AI picks up." },
      { who: "agent",  txt: "Bright Smile Dental, this is Eva. How can I help today?" },
      { who: "caller", txt: "I cracked a tooth last night. Is there any chance someone can see me today?", extract: { reason: "Cracked tooth · emergency" } },
      { who: "agent",  txt: "That sounds painful — let me see. Dr. Patel can squeeze you in at 2:30pm today." },
      { who: "agent",  txt: "Can I grab your full name and date of birth so I can pull your chart?" },
      { who: "caller", txt: "Layla Mendoza, Aug 12, 1991.", extract: { name: "Layla Mendoza", dob: "Aug 12, 1991", apt_date: "Today · Apr 13", apt_time: "2:30 PM", clinician: "Dr. Patel" } },
      { who: "agent",  txt: "Found you. Can I confirm your email and phone on file?" },
      { who: "caller", txt: "layla.m@outlook.com — 647-555-0844.", extract: { email: "layla.m@outlook.com", phone: "+1 (647) 555-0844" } },
      { who: "agent",  txt: "Confirmed. I'll email the appointment details and aftercare tips. Try to keep ice off it and avoid chewing on that side." },
    ],
    iq: {
      classification: "booked-emergency",
      answered_by:    "AI",
      patient_type:   "existing",
      booking_type:   "Emergency Visit",
      summary: "Layla Mendoza — existing patient — called with a cracked tooth. AI triaged it as emergency, booked Dr. Patel today at 2:30 PM, and emailed appointment + aftercare info. Call closed in 1m 09s.",
    },
  },
  spa: {
    id: "spa",
    label: "Spa & Wellness",
    Icon: Smile,
    accent: { from: "#a855f7", to: "#ec4899" },
    blurb: "Luxury-spa booking with upsell + confirmation email.",
    voice: "Naya · warm, hospitable",
    business: "Sereno Day Spa",
    transcript: [
      { who: "ring",   txt: "Ring 1…" },
      { who: "ring",   txt: "Ring 2…" },
      { who: "ring",   txt: "Ring 3 — AI picks up." },
      { who: "agent",  txt: "Sereno Day Spa, this is Naya — how can I make your day better?" },
      { who: "caller", txt: "I'd love a 60-minute couples massage this Saturday afternoon.", extract: { reason: "60-min couples massage" } },
      { who: "agent",  txt: "Saturday 3pm in our Garden Suite is open. Would you like to add warm-stone for $25 per person?" },
      { who: "caller", txt: "Yes please.", extract: { upsell: "Warm-stone upgrade · +$50", apt_date: "Sat · Apr 18", apt_time: "3:00 PM" } },
      { who: "agent",  txt: "Wonderful. Can I get both names and a good phone number?" },
      { who: "caller", txt: "Sophia & Daniel Reyes — 416-555-0107.", extract: { name: "Sophia & Daniel Reyes", phone: "+1 (416) 555-0107" } },
      { who: "agent",  txt: "And the best email for the confirmation?" },
      { who: "caller", txt: "sophia.reyes@gmail.com", extract: { email: "sophia.reyes@gmail.com" } },
      { who: "agent",  txt: "All set. We'll email the booking, parking link and arrival tips. See you Saturday." },
    ],
    iq: {
      classification: "booked-with-upsell",
      answered_by:    "AI",
      patient_type:   "new",
      booking_type:   "60-min Couples Massage",
      summary: "Sophia & Daniel Reyes — new guests — booked a 60-min couples massage for Sat 3:00 PM with the warm-stone upgrade. AI captured names + contact and emailed confirmation. Call closed in 1m 32s.",
    },
  },
  repair: {
    id: "repair",
    label: "Auto Repair",
    Icon: Wrench,
    accent: { from: "#f97316", to: "#ef4444" },
    blurb: "Service booking, vehicle capture, ETA + email quote.",
    voice: "Carl · friendly, no-nonsense",
    business: "Northgate Auto Care",
    transcript: [
      { who: "ring",   txt: "Ring 1…" },
      { who: "ring",   txt: "Ring 2…" },
      { who: "ring",   txt: "Ring 3 — AI picks up." },
      { who: "agent",  txt: "Northgate Auto Care, this is Carl — how can I help?" },
      { who: "caller", txt: "My brakes are squealing. How soon can you take a look?", extract: { reason: "Brakes squealing" } },
      { who: "agent",  txt: "Could be pads. What's the make, model and year?" },
      { who: "caller", txt: "2018 Honda Civic.", extract: { vehicle: "2018 Honda Civic" } },
      { who: "agent",  txt: "Got it. I can squeeze you in tomorrow 9am for an inspection — about $35, takes 30 minutes." },
      { who: "caller", txt: "Yes, book it. Name's Aaron Tate, phone 905-555-0144.", extract: { name: "Aaron Tate", phone: "+1 (905) 555-0144", apt_date: "Tomorrow · Apr 14", apt_time: "9:00 AM" } },
      { who: "agent",  txt: "And the best email so I can send the booking + quote?" },
      { who: "caller", txt: "aaron.tate@gmail.com", extract: { email: "aaron.tate@gmail.com" } },
      { who: "agent",  txt: "Booked. I'll email the appointment and follow up with a quote after we take a look." },
    ],
    iq: {
      classification: "booked-inspection",
      answered_by:    "AI",
      patient_type:   "new",
      booking_type:   "Brake Inspection · $35",
      summary: "Aaron Tate booked a brake inspection on his 2018 Honda Civic for tomorrow 9 AM at $35. AI captured contact + vehicle and emailed the booking. Quote will follow after inspection. Call closed in 1m 26s.",
    },
  },
};

const INDUSTRY_LIST = ["physio", "dental", "spa", "repair"];

export const VoiceAIModal = ({ open, onClose, scenarioLabel }) => {
  const [step, setStep] = useState("pick"); // 'pick' | 'play' | 'call' | 'submitted'
  const [industryId, setIndustryId] = useState("physio");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("pick"); setIndustryId("physio"); setPhone(""); setName(""); setBusy(false); };
  useEffect(() => { if (!open) setTimeout(reset, 250); }, [open]);

  const industry = INDUSTRIES[industryId];

  const requestCall = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name || "KAI voice request",
          email: `voice+${Date.now()}@clinic-os.placeholder`,
          phone,
          clinic_name: industry.business,
          message: `Voice AI ${industry.label} demo request from KAI Experience · scenario: ${scenarioLabel || "n/a"}`,
          source: `kai-voice-call-${industry.id}`,
        }),
      });
      setStep("submitted");
    } catch (_e) {
      setStep("submitted");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={() => onClose()}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-3 backdrop-blur-sm sm:p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="voice-ai-modal"
          className="relative max-h-[94vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-border bg-background shadow-2xl"
        >
          <button
            type="button"
            onClick={() => onClose()}
            aria-label="Close"
            data-testid="voice-modal-close"
            className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/80 ring-1 ring-inset ring-border transition-colors hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Header */}
          <div className="border-b border-border bg-gradient-to-br from-violet-600/15 via-fuchsia-600/10 to-rose-500/10 px-6 py-4 sm:px-8 sm:py-5">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-md ring-1 ring-inset ring-white/15">
                <Headphones className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
                  Voice AI · KAI
                </p>
                <h3 className="mt-0.5 font-display text-2xl font-black tracking-tight sm:text-3xl">
                  Watch KAI handle an inbound call · end to end
                </h3>
              </div>
            </div>
            <p className="mt-2 max-w-3xl text-[12.5px] leading-snug text-muted-foreground sm:text-[13px]">
              Pick an industry — KAI answers after 3 rings, collects the booking, classifies the call, and emails the customer a confirmation. Real voice agents plug in here — same UX.
            </p>
          </div>

          {/* Body */}
          <div className="p-5 sm:p-6 lg:p-8">
            {step === "pick"      && <PickIndustry pick={(id) => { setIndustryId(id); setStep("play"); }} />}
            {step === "play"      && <PlayCall industry={industry} onBack={() => setStep("pick")} onCallMe={() => setStep("call")} />}
            {step === "call"      && <CallMeForm industry={industry} onBack={() => setStep("play")} onSubmit={requestCall} phone={phone} setPhone={setPhone} name={name} setName={setName} busy={busy} />}
            {step === "submitted" && <Submitted phone={phone} industry={industry} onClose={onClose} />}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
const PickIndustry = ({ pick }) => (
  <div>
    <p className="text-center text-[10.5px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
      Step 1 of 2
    </p>
    <h4 className="mt-2 text-center font-display text-xl font-black tracking-tight sm:text-2xl">
      Which industry feels closest to yours?
    </h4>
    <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {INDUSTRY_LIST.map((id) => {
        const i = INDUSTRIES[id];
        return (
          <button
            key={id}
            type="button"
            onClick={() => pick(id)}
            data-testid={`voice-industry-${id}`}
            className="group relative overflow-hidden rounded-2xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-1 hover:border-foreground/30 hover:shadow-md"
          >
            <div
              className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-25 blur-3xl transition-opacity group-hover:opacity-50"
              style={{ backgroundImage: `linear-gradient(135deg, ${i.accent.from}, ${i.accent.to})` }}
            />
            <span
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15"
              style={{ backgroundImage: `linear-gradient(135deg, ${i.accent.from}, ${i.accent.to})` }}
            >
              <i.Icon className="h-5 w-5" />
            </span>
            <p className="relative mt-3 font-display text-[15px] font-black tracking-tight">{i.label}</p>
            <p className="relative mt-0.5 text-[11.5px] leading-snug text-muted-foreground">{i.blurb}</p>
            <span className="relative mt-2.5 inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: i.accent.from }}>
              Hear the call <ArrowRight className="h-3 w-3" />
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────── */
const EMPTY_FIELDS = { name: "—", email: "—", phone: "—", apt_date: "—", apt_time: "—", reason: "—" };

const PlayCall = ({ industry, onBack, onCallMe }) => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [showSummary, setShowSummary] = useState(false); // 3rd-step toggle
  const [countdown, setCountdown] = useState(null); // 3,2,1 then auto-advance
  const timer = useRef(null);
  const cdTimer = useRef(null);
  const transcriptRef = useRef(null);
  const gradient = `linear-gradient(135deg, ${industry.accent.from}, ${industry.accent.to})`;
  const isComplete = idx >= industry.transcript.length;

  useEffect(() => {
    if (!playing) return undefined;
    if (idx >= industry.transcript.length) { setPlaying(false); return undefined; }
    const step = industry.transcript[idx];
    const delay = step.who === "ring" ? 800 : 1400;
    timer.current = setTimeout(() => {
      if (step.extract) setFields((f) => ({ ...f, ...step.extract }));
      setIdx((i) => i + 1);
    }, idx === 0 ? 400 : delay);
    return () => clearTimeout(timer.current);
  }, [idx, playing, industry]);

  // Auto-scroll the transcript window as messages arrive
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [idx]);

  // After call ends, start 3-2-1 countdown then auto-advance to summary
  useEffect(() => {
    if (!isComplete || showSummary || countdown !== null) return undefined;
    setCountdown(3);
    return undefined;
  }, [isComplete, showSummary, countdown]);

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown <= 0) { setShowSummary(true); setCountdown(null); return undefined; }
    cdTimer.current = setTimeout(() => setCountdown((c) => c - 1), 800);
    return () => clearTimeout(cdTimer.current);
  }, [countdown]);

  const replay = () => {
    clearTimeout(timer.current);
    clearTimeout(cdTimer.current);
    setIdx(0); setFields(EMPTY_FIELDS); setShowSummary(false); setCountdown(null); setPlaying(true);
  };

  const skipCountdown = () => { clearTimeout(cdTimer.current); setShowSummary(true); setCountdown(null); };

  return (
    <div>
      {/* Step tracker */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em]">
          <StepDot n={1} active label="Pick industry"  done />
          <StepLine done />
          <StepDot n={2} active={!showSummary} done={isComplete} label="Live call" />
          <StepLine done={isComplete} active={!showSummary && isComplete} />
          <StepDot n={3} active={showSummary} done={false} label="AI Summary + Email" muted={!isComplete} />
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground">
            ← Industry
          </button>
          <button type="button" onClick={replay} className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-2.5 py-1 text-[11px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30">
            <RefreshCw className="h-3 w-3" /> Replay
          </button>
        </div>
      </div>

      {/* MAIN: Step 2 (call + fields) OR Step 3 (summary + email) */}
      <div className="mt-4">
        {!showSummary ? (
          /* STEP 2 — Transcript (auto-scroll) + KAI Intelligence */
          <motion.div
            key="step2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="grid gap-4 lg:grid-cols-12 lg:gap-5"
          >
            {/* Transcript */}
            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <div className="flex items-center gap-3 border-b border-border px-4 py-3" style={{ background: `linear-gradient(135deg, ${industry.accent.from}15, ${industry.accent.to}15)` }}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
                    <industry.Icon className="h-5 w-5" />
                  </span>
                  <div className="flex-1">
                    <p className="font-display text-[13.5px] font-black tracking-tight">{industry.label} · {industry.business}</p>
                    <p className="text-[11px] text-muted-foreground">Voice: {industry.voice}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    {isComplete ? "Call ended" : "Live"}
                  </span>
                </div>

                <div ref={transcriptRef} className="h-[340px] space-y-2 overflow-y-auto p-4 scroll-smooth">
                  {industry.transcript.slice(0, idx).map((m, i) => {
                    if (m.who === "ring") {
                      return (
                        <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300">
                            <Phone className="h-3 w-3 animate-pulse" />
                            {m.txt}
                          </span>
                        </motion.div>
                      );
                    }
                    return (
                      <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.who === "caller" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={
                            m.who === "agent"
                              ? "max-w-[80%] rounded-2xl rounded-tl-sm border border-border bg-secondary/40 px-3 py-2 text-[13px] leading-snug"
                              : "max-w-[80%] rounded-2xl rounded-tr-sm px-3 py-2 text-[13px] font-medium leading-snug text-white shadow-sm"
                          }
                          style={m.who === "caller" ? { backgroundImage: gradient } : undefined}
                        >
                          <p className="mb-0.5 text-[9.5px] font-bold uppercase tracking-[0.14em] opacity-70">
                            {m.who === "agent" ? "KAI" : "Caller"}
                          </p>
                          {m.txt}
                        </div>
                      </motion.div>
                    );
                  })}
                  {playing && !isComplete && (
                    <div className="flex justify-start pl-3 text-[11px] text-muted-foreground">
                      <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> KAI is speaking…
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Live fields */}
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <div className="border-b border-border bg-gradient-to-r from-violet-500/[0.10] to-fuchsia-500/[0.10] px-4 py-3">
                  <p className="inline-flex items-center gap-1.5 font-display text-[12.5px] font-black tracking-tight text-violet-700 dark:text-violet-300">
                    <Sparkles className="h-3.5 w-3.5" />
                    KAI Intelligence · live fields
                  </p>
                  <p className="mt-0.5 text-[10.5px] text-muted-foreground">Extracted in real time from the call.</p>
                </div>
                <div className="grid grid-cols-1 gap-2 p-4">
                  {[
                    { k: "Name",   v: fields.name,    Icon: User },
                    { k: "Email",  v: fields.email,   Icon: Mail },
                    { k: "Phone",  v: fields.phone,   Icon: Phone },
                    { k: "Date",   v: fields.apt_date, Icon: Calendar },
                    { k: "Time",   v: fields.apt_time, Icon: Clock },
                    { k: "Reason", v: fields.reason,  Icon: Pencil },
                  ].map((row) => {
                    const filled = row.v && row.v !== "—";
                    return (
                      <motion.div
                        key={row.k}
                        layout
                        className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 transition-colors ${filled ? "border-emerald-500/30 bg-emerald-500/[0.06]" : "border-border bg-secondary/30"}`}
                      >
                        <row.Icon className={`h-3.5 w-3.5 flex-none ${filled ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} />
                        <p className="w-12 flex-none text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{row.k}</p>
                        <p className={`flex-1 truncate text-[12px] font-semibold ${filled ? "text-foreground" : "text-muted-foreground"}`}>{row.v}</p>
                        {filled && <CheckCircle2 className="h-3.5 w-3.5 flex-none text-emerald-500" />}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          /* STEP 3 — AI Summary + Email to clinic */
          <motion.div
            key="step3"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="grid gap-4 lg:grid-cols-2 lg:gap-5"
          >
            {/* AI Summary */}
            <div className="overflow-hidden rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] shadow-sm">
              <div className="flex items-center gap-2 border-b border-amber-500/20 px-4 py-3">
                <Sparkles className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                <p className="font-display text-[13px] font-black tracking-tight text-amber-700 dark:text-amber-300">KAI AI Summary</p>
              </div>
              <div className="grid grid-cols-2 gap-2 p-4">
                <IQBadge k="Classification" v={industry.iq.classification} tone="emerald" />
                <IQBadge k="Answered by"    v={industry.iq.answered_by}    tone="violet" />
                <IQBadge k="Patient type"   v={industry.iq.patient_type}   tone="indigo" />
                <IQBadge k="Booking type"   v={industry.iq.booking_type}   tone="sky" />
              </div>
              <div className="border-t border-amber-500/20 px-4 pb-4 pt-3">
                <p className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Summary</p>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-foreground/90">{industry.iq.summary}</p>
              </div>
            </div>

            {/* Email to clinic */}
            <div className="overflow-hidden rounded-2xl border border-sky-500/30 bg-sky-500/[0.06] shadow-sm">
              <div className="flex items-center gap-2 border-b border-sky-500/20 px-4 py-3">
                <MailCheck className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
                <p className="font-display text-[13px] font-black tracking-tight text-sky-700 dark:text-sky-300">Email sent to clinic · preview</p>
              </div>
              <div className="p-4">
                <div className="space-y-1 border-b border-border/60 pb-2.5 text-[11.5px]">
                  <p><strong className="font-bold">To:</strong> <span className="font-mono">front-desk@{industry.business.toLowerCase().split(" · ")[0].replace(/[^a-z]+/g, "")}.com</span></p>
                  <p><strong className="font-bold">From:</strong> <span className="font-mono">kai@clinic-os.com</span></p>
                  <p><strong className="font-bold">Subject:</strong> ✓ New booking from KAI — {fields.name === "—" ? "caller" : fields.name}</p>
                </div>
                <div className="mt-2.5 space-y-1.5 text-[11.5px] leading-relaxed text-foreground/90">
                  <p>Hi team — KAI just booked a new appointment for you.</p>
                  <div className="rounded-lg border border-border bg-card p-2.5 text-[11px]">
                    <p><strong className="font-bold">Customer:</strong> {fields.name} · {fields.phone}</p>
                    <p><strong className="font-bold">Email:</strong> {fields.email}</p>
                    <p><strong className="font-bold">Service:</strong> {industry.iq.booking_type}</p>
                    <p><strong className="font-bold">When:</strong> {fields.apt_date} · {fields.apt_time}</p>
                    <p><strong className="font-bold">Patient type:</strong> {industry.iq.patient_type}</p>
                    <p><strong className="font-bold">Reason:</strong> {fields.reason}</p>
                  </div>
                  <p className="mt-1.5">Full transcript and intake form are attached.<span className="ml-1 text-muted-foreground"> — KAI</span></p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Action row */}
      <div className="mt-4 grid gap-2 sm:grid-cols-12">
        {/* Auto-advance countdown OR back button */}
        {isComplete && !showSummary && countdown !== null && (
          <button
            type="button"
            onClick={skipCountdown}
            data-testid="voice-step-countdown"
            className="relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 text-[13px] font-bold text-amber-700 transition-all hover:-translate-y-0.5 hover:bg-amber-500/20 dark:text-amber-300 sm:col-span-4"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>Showing AI Summary + Email in</span>
            <motion.span
              key={countdown}
              initial={{ scale: 1.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35, type: "spring", stiffness: 380, damping: 18 }}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 font-mono text-[15px] font-black text-white shadow-md"
            >
              {countdown}
            </motion.span>
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-amber-700/80 dark:text-amber-300/80">tap to skip</span>
          </button>
        )}
        {isComplete && showSummary && (
          <button
            type="button"
            onClick={() => setShowSummary(false)}
            data-testid="voice-step-back"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 text-[13px] font-bold text-amber-700 transition-all hover:-translate-y-0.5 hover:bg-amber-500/20 dark:text-amber-300 sm:col-span-4"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to call
          </button>
        )}
        <button
          type="button"
          disabled
          data-testid="voice-talk-start"
          className={`inline-flex h-12 cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 text-sm font-bold text-white opacity-70 shadow-lg ring-1 ring-inset ring-white/15 ${isComplete ? "sm:col-span-4" : "sm:col-span-6"}`}
        >
          <Mic className="h-4 w-4" />
          Start live conversation
          <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]">Soon</span>
        </button>
        <button
          type="button"
          onClick={onCallMe}
          data-testid="voice-call-cta"
          className={`inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-violet-500/50 bg-violet-500/10 px-6 text-sm font-bold text-violet-700 transition-all hover:-translate-y-0.5 hover:bg-violet-500/15 dark:text-violet-300 ${isComplete ? "sm:col-span-4" : "sm:col-span-6"}`}
        >
          <PhoneOutgoing className="h-4 w-4" />
          Get a call from KAI
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

/* Step dot pill */
const StepDot = ({ n, active, done, label, muted }) => (
  <span
    className={
      done
        ? "inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-emerald-700 dark:text-emerald-300"
        : active
          ? "inline-flex items-center gap-1.5 rounded-full bg-violet-500/15 px-2.5 py-1 text-violet-700 dark:text-violet-300"
          : muted
            ? "inline-flex items-center gap-1.5 rounded-full bg-secondary/40 px-2.5 py-1 text-muted-foreground opacity-50"
            : "inline-flex items-center gap-1.5 rounded-full bg-secondary/40 px-2.5 py-1 text-muted-foreground"
    }
  >
    <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold ${done ? "bg-emerald-500 text-white" : active ? "bg-violet-500 text-white" : "bg-border text-foreground/50"}`}>{done ? "✓" : n}</span>
    {label}
  </span>
);
const StepLine = ({ done, active }) => (
  <span className={`h-px w-4 ${done ? "bg-emerald-500" : active ? "bg-violet-500" : "bg-border"}`} />
);

const IQBadge = ({ k, v, tone }) => {
  const map = {
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    violet:  "border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300",
    indigo:  "border-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
    sky:     "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  };
  return (
    <div className="rounded-lg border border-border bg-card p-2">
      <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{k}</p>
      <p className={`mt-1 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10.5px] font-bold ${map[tone] || ""}`}>{v}</p>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
const CallMeForm = ({ industry, onBack, onSubmit, phone, setPhone, name, setName, busy }) => (
  <form onSubmit={onSubmit} className="mx-auto max-w-md">
    <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground">
      ← Back
    </button>
    <h4 className="mt-3 font-display text-xl font-black tracking-tight sm:text-2xl">
      Get a call from KAI · {industry.label}
    </h4>
    <p className="mt-1 text-[12.5px] leading-snug text-muted-foreground">
      We&apos;ll dial you in ~10 seconds. KAI will pick up using {industry.voice}.
    </p>

    <div className="mt-5 grid gap-3">
      <label className="block">
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Your name</span>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr. Sarah Chen" data-testid="voice-call-name" className="mt-1.5 block w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none ring-ring transition-all focus:ring-2" />
      </label>
      <label className="block">
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Phone *</span>
        <input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 555-5555" data-testid="voice-call-phone" className="mt-1.5 block w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none ring-ring transition-all focus:ring-2" />
      </label>
    </div>

    <button
      type="submit"
      disabled={busy || !phone.trim()}
      data-testid="voice-call-submit"
      style={{ backgroundImage: `linear-gradient(135deg, ${industry.accent.from}, ${industry.accent.to})` }}
      className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-7 text-sm font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
    >
      {busy ? (<><Loader2 className="h-4 w-4 animate-spin" />Sending…</>) : (<>Call me in ~10 seconds<ArrowRight className="h-4 w-4" /></>)}
    </button>

    <p className="mt-4 text-center text-[11px] leading-snug text-muted-foreground">
      Hook your dialer (Vapi · Retell · Bland · Twilio) by wiring{" "}
      <code className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[11px]">POST /api/kai/voice/outbound</code>.
    </p>
  </form>
);

const Submitted = ({ phone, industry, onClose }) => (
  <div className="py-4 text-center">
    <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
      <CheckCircle2 className="h-7 w-7" />
    </span>
    <h4 className="mt-5 font-display text-2xl font-black tracking-tight">You&apos;re on the list.</h4>
    <p className="mx-auto mt-3 max-w-sm text-[13.5px] leading-relaxed text-muted-foreground">
      We&apos;ve queued an outbound <strong className="text-foreground/90">{industry.label}</strong> call to{" "}
      <strong className="text-foreground/90">{phone || "your number"}</strong>. The moment we connect a voice agent, you&apos;ll be among the first to get the live call.
    </p>
    <button type="button" onClick={onClose} className="mt-6 inline-flex h-11 items-center gap-2 rounded-xl border border-border bg-card px-6 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30">
      Close
    </button>
  </div>
);
